import { SpaceBetween } from '@amzn/awsui-components-core';
import {
  ContentAudience,
  ContentWorkflow,
  PublicationType,
  UserRoles,
  WorkflowState,
} from '@amzn/awscat-pattern-library-service-model';
import React, { type JSX } from 'react';
import { autoGenerateHref } from '../../api/ContentUtil';
import constants from '../../common/constants';
import { getContentPath } from '../../common/ContentPaths';
import RouterPaths from '../../common/RouterPaths';
import { withAppContext } from '../../contexts/AppContext';
import ModalDialogButtonV3 from '../common/ModalDialogButtonV3';
import BrowserHistory from '../utils/BrowserHistory';
import { FinalizeButton } from './FinalizeButton';
import PublicationViewer from './PublicationViewer';
import { CopyFinalizingLinkButton } from './CopyFinalizingLinkButton';
import { withRouter } from 'react-router';
import { SkipNextDraftReviewButton } from './SkipNextDraftReviewButton';
import { getFinalizingInlineComments } from '../utils/InlineCommentUtils';

class FinalizingViewer extends PublicationViewer {
  protected showComment = false;
  protected showTechwriterMetadata = true;
  protected allowedEditing = true;
  protected hideEmptySections = false;
  protected showSectionalComments = true;
  protected showLastBarRaiserEvaluation: boolean = true;
  protected showCodeValidationError: boolean = false;

  protected getBreadcrumbItems(): Array<{ text: string; href: string }> {
    return [
      {
        href: `${RouterPaths.TECH_WRITER_DASHBOARD_NEW}`,
        text: constants.FINALIZE_PENDDING_CONTENTS,
      },
    ];
  }

  protected renderControls(): false | JSX.Element {
    const { contentId } = this.props.match.params;
    const { isEditingMode, publication } = this.state;
    // @ts-expect-error TS2339 - strict migration
    const { userProfile } = this.props.appContext.authnInfo;
    const { userId, roles } = userProfile;
    const publicationType = publication && publication.publicationType;
    const contentAudience = publication && publication.contentAudience;
    const buttonDisabled =
      isEditingMode ||
      ![PublicationType.INTERNAL, PublicationType.FINALIZING].some((t) => t === publicationType) ||
      contentAudience === ContentAudience.INTERNAL;

    const reverseButton =
      publicationType === PublicationType.FINALIZING ? (
        <ModalDialogButtonV3
          header={'Undo editing'}
          text={
            'You have modified the original publication for finalization. Do you want to undo this and go back to original version?'
          }
          onSubmit={this.reverseFinalizing}
          submitButtonText={'Undo editing'}
          disabled={buttonDisabled}
        />
      ) : null;

    const skipNextDraftReviewButton = [UserRoles.Admin, UserRoles.TechWriter].some((r) =>
      roles.has(r),
    ) ? (
      <SkipNextDraftReviewButton
        contentId={contentId}
        // @ts-expect-error TS2532 - strict migration
        draftWorkflowId={this.state.workflow.draftWorkflowId}
        disabled={
          this.state.publication?.publicationState !== WorkflowState.PUBLISHED ||
          !!this.state.workflow?.skipDraftReview
        }
      />
    ) : null;

    const finalizeButton = [UserRoles.Admin, UserRoles.TechWriter].some((r) => roles.has(r)) ? (
      <FinalizeButton
        contentId={contentId}
        workflowId={this.parsedWorkflowId}
        publicationType={publicationType}
        userId={userId}
        disabled={buttonDisabled}
        afterFinalizedSuccess={this.nagivateToPublicationView}
        publication={this.state.publication}
      />
    ) : null;

    const shareChangesButton =
      // @ts-expect-error TS2532 - strict migration
      this.state.workflow.publicationState === WorkflowState.FINALIZING ? (
        <CopyFinalizingLinkButton
          // @ts-expect-error TS2532 - strict migration
          contentId={this.state.workflow.contentId}
          // @ts-expect-error TS2532 - strict migration
          workflowId={this.state.workflow.publicationWorkflowId}
        />
      ) : null;

    return (
      <SpaceBetween direction="horizontal" size="xs">
        {shareChangesButton}
        {reverseButton}
        {skipNextDraftReviewButton}
        {finalizeButton}
      </SpaceBetween>
    );
  }

  // @ts-expect-error TS2416 - strict migration
  protected reloadData = async () => {
    const { contentId, workflowId } = this.props.match.params;
    const parsedWorkflowId = parseInt(workflowId, constants.DECIMAL);

    this.setState({
      isLoading: true,
    });

    const [
      { workflows: workflowHistory },
      { accessUsers: coAuthors },
      { accessUsers: peerReviewers },
      { workflow },
    ] = await Promise.all([
      this.mplServiceClient.listWorkflowHistory(contentId),
      this.mplServiceClient.listContentSharedWith(contentId),
      this.mplServiceClient.listContentReviewers(contentId),
      this.mplServiceClient.getContentWorkflow(contentId),
    ]);
    const classifier =
      workflow.publicationState === WorkflowState.FINALIZING ? 'finalizing' : undefined;
    const publication = await this.mplServiceClient.getPublicationByWorkflow(
      contentId,
      parsedWorkflowId,
      classifier,
    );
    // Auto generate href for each content node if they are not existed.
    autoGenerateHref(publication.content.contentData, publication.content?.contentMetadata);
    const { inlineComments: originalInlineComments } =
      await this.mplServiceClient.listInlineComments(contentId, parsedWorkflowId);
    const finalizingInlineComments = getFinalizingInlineComments(originalInlineComments);

    this.setState({
      coAuthors,
      peerReviewers,
      isLoading: false,
      publication,
      showSectionalComments: true,
      workflow,
      workflowHistory,
      workflowId: parsedWorkflowId,
      inlineComments: finalizingInlineComments,
    });

    if (publication.publicationType === PublicationType.FINALIZING) {
      this.flashbarMessageBroker.pushInfoMessage(
        `The original publication has been modified for finalization.`,
        15000,
      );
    }

    // Check the fetched workflow, not this.state: the setState above is batched and not yet applied.
    if (this.isDraftWorkflowUnchanged(workflow) && !this.showContentControl) {
      this.showContentControl = true;
      this.forceUpdate();
    } else {
      this.flashbarMessageBroker.pushInfoMessage(
        `The draft version has been modified, editing controls are disabled.`,
        15000,
      );
    }
  };

  private reverseFinalizing = async () => {
    const { contentId, workflowId } = this.props.match.params;
    const parsedWorkflowId = parseInt(workflowId, constants.DECIMAL);
    await this.mplServiceClient.reverseFinalizing(contentId, parsedWorkflowId);
    const { inlineComments } = await this.mplServiceClient.listInlineComments(
      contentId,
      parsedWorkflowId,
    );
    const updateCommentAttributes = inlineComments
      .filter((comment) => comment.finalizingStartOffset)
      .map((comment) => ({
        commentId: comment.commentId,
        finalizingStartOffset: 0,
        finalizingEndOffset: 0,
        finalizingTextData: '',
      }));
    if (updateCommentAttributes.length) {
      await this.mplServiceClient.updateInlineComments(
        contentId,
        parsedWorkflowId,
        updateCommentAttributes,
      );
    }
    await this.reloadData();
  };

  private nagivateToPublicationView = async () => {
    const { contentId, workflowId } = this.props.match.params;
    this.flashbarMessageBroker.pushSuccessMessage(`The content has been finalized successfully`);
    BrowserHistory.push(getContentPath({ contentId, mode: 'publication' }), {
      keepFlashbarMessages: true,
    });
  };

  private isDraftWorkflowUnchanged = (workflow: ContentWorkflow) => {
    return !workflow.isLocked && workflow.draftState === WorkflowState.UNMODIFIED;
  };
}

export default withRouter(withAppContext(FinalizingViewer));
